import { frameId } from "../assets/manifest.ts";
import type { CompileContext } from "../compile/context.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { MapDocument, TileRef } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";

/**
 * The Issues kind of a map's props whose frame the compile context cannot size.
 */
export const FRAME_MISSING_KIND = "prop.frame-missing";

/**
 * One warning for a map whose props show frames the compile context cannot size: a pack the server does not hold
 * (a studio pack drawn elsewhere and never brought to it), or one changed in another tab. The editor draws each such
 * prop as a one-tile box and picks it over that box, and no check can measure it, so the warning names how many props,
 * which packs, and goes to the first. A context that carries no frame sizes at all is not read.
 *
 * @param map The map.
 * @param context The compile context's frame sizes and footprints.
 * @returns At most one issue.
 */
export function missingFrameIssues(map: MapDocument, context: Pick<CompileContext, "collision" | "frameSizes">): ValidationIssue[] {
  if (context.frameSizes === undefined) {
    return [];
  }
  let first: { readonly layerId: string; readonly propId: string; readonly frame: TileRef } | null = null;
  let count = 0;
  const tilesets: string[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile)) !== undefined) {
        continue;
      }
      count += 1;
      first ??= { layerId: layer.id, propId: prop.id, frame: { tileset: prop.frame.tileset, tile: prop.frame.tile } };
      if (!tilesets.includes(prop.frame.tileset)) {
        tilesets.push(prop.frame.tileset);
      }
    }
  }
  if (first === null) {
    return [];
  }
  return [{ mapId: map.id, layerId: first.layerId, propId: first.propId, kind: FRAME_MISSING_KIND, severity: "warning", frame: first.frame, message: `${count} prop${count === 1 ? "" : "s"} on this map show${count === 1 ? "s" : ""} frames this server does not hold (${tilesets.join(", ")}); they draw as boxes and cannot be measured until their packs are on the server` }];
}
